<script lang="ts">
	import type { OrderConfigurationOrder } from '$models/OrderConfigurator/OrderConfigurator';
	import type { OrderConfiguratorDefault } from '$models/OrderConfigurator/OrderConfiguratorDefault';
	import { page } from '$app/state';
	import QuantityValidationMessage from '$components/Quantity/QuantityValidationMessage.svelte';
	import VatLabel from '$components/Shop/VatLabel.svelte';
	import RangeSlider from '$components/UI/FormElements/RangeSlider.svelte';
	import PortionSuggestions from '$components/UI/Tooltips/PortionSuggestions.svelte';
	import { getSiteContext } from '$lib/context/site';
	import { getShopPresentation } from '$lib/shop/presentation.svelte';
	import { getConfiguratorState } from '$lib/state/configurator.svelte';
	import { formatCurrency, formatNumericString } from '$lib/utils';

	interface Props {
		configurator: OrderConfiguratorDefault;
		onSubmit: (order: OrderConfigurationOrder) => void;
	}

	let { configurator, onSubmit }: Props = $props();

	const configuratorState = getConfiguratorState();
	const presentation = getShopPresentation();
	const site = getSiteContext();
	const translations = page.data.page.layout.translations;

	// Assign some local variables for ergonomics and readability
	let quantities = $derived(configuratorState.quantities());
	let quantityMultiplier = $derived(configuratorState.quantityMultiplier());
	let prices = $derived(configuratorState.displayPrices(false, presentation.vat));
	let fromPrices = $derived(configuratorState.displayPrices(true, presentation.vat));
	let selectedCategory = $derived(configuratorState.selectedCategory());
	let selectedQuantity = $derived(configuratorState.selectedQuantity());
	let selectedDisplayQuantity = $derived(selectedQuantity * quantityMultiplier);
	let fromPriceQuantity = $derived(configuratorState.fromPriceQuantity());
	let fromDisplayQuantity = $derived((fromPriceQuantity ?? 0) * quantityMultiplier);

	// Local state
	let targetQuantity: number = $state(getStartingTargetQuantity());

	// Make sure the starting target quantity is a valid quantity
	function getStartingTargetQuantity() {
		if (!quantities.includes(selectedQuantity) && quantities.length > 0) {
			return quantities[0];
		}

		return selectedQuantity;
	}

	// Local event handlers
	function handleSubmit() {
		onSubmit(configuratorState.order());
	}
</script>

<div class="aio-step aio-step--v2">
	{#if configurator.sidebarTitle && configurator.sidebarTitle.length && configurator.sidebarText && configurator.sidebarText.length}
		<div class="aio-step-info">
			<h2 class="aio-step-info-title">
				{configurator.sidebarTitle}
			</h2>
			<div class="aio-step-info-text rich-text">
				{@html configurator.sidebarText}
			</div>
		</div>
	{/if}

	<div class="aio-step-main">
		<h3 class="aio-step-main-title">
			{#if configuratorState.selectedFrequency() === 0}
				{configurator.selectFieldsOnetimeLabel}
			{:else}
				{configurator.selectFieldsLabel}
			{/if}
		</h3>
		<div class="aio-step-header">
			<div class="aio-step-header-selects" data-target-container>
				<select
					class="aio-step-header-select"
					name="targetFrequency"
					onchange={(e) => configuratorState.setFrequency(parseInt(e.target.value))}
					disabled={configuratorState.frequencies().length === 1}
				>
					{#each configuratorState.frequencies() as frequency}
						<option value={frequency} selected={configuratorState.selectedFrequency() == frequency}>
							{#if frequency == 0}
								{translations.frequencyOnetime}
							{:else}
								{formatNumericString(
									translations.frequencySingle,
									translations.frequencyMultiple,
									frequency,
									'%quantity%'
								)}
							{/if}
						</option>
					{/each}
				</select>
				<select
					class="aio-step-header-select"
					name="targetQuantity"
					onchange={(e) => (targetQuantity = parseInt(e.target.value))}
					disabled={configuratorState.quantities().length === 1}
				>
					{#each configuratorState.quantities() as quantity}
						<option value={quantity} selected={targetQuantity == quantity}>
							{formatNumericString(
								translations.productAmountTextSingle.replaceAll(
									'%product%',
									selectedCategory?.translations?.unitNameSingle ?? '%product%'
								),
								translations.productAmountTextMultiple.replaceAll(
									'%products%',
									selectedCategory?.translations?.unitNamePlural ?? '%products%'
								),
								quantity * configuratorState.quantityMultiplier(),
								'%quantity%'
							)}
						</option>
					{/each}
				</select>
			</div>
			{#if targetQuantity > 0}
				<div class="aio-step-header-price">
					<VatLabel />
					{#if prices}
						{#if configuratorState.selectedFrequency() > 0 && prices.recurring !== null}
							{#if prices.recurringDiscount > 0}
								<div class="aio-step-header-price-value aio-step-header-price-value--discount">
									<small>{translations.discountPriceLabel}</small>
									{formatCurrency(prices.recurringPayable!, site.locale.code)}
									<small>
										{translations.discountRegularPriceLabel.replace(
											'%price%',
											formatCurrency(prices.recurring, site.locale.code)
										)}
									</small>
								</div>
							{:else}
								<div class="aio-step-header-price-value">
									{formatCurrency(prices.recurring, site.locale.code)}
								</div>
							{/if}
						{:else if configuratorState.selectedFrequency() === 0 && prices.onetime !== null}
							{#if prices.onetimeDiscount > 0}
								<div class="aio-step-header-price-value aio-step-header-price-value--discount">
									<small>{translations.discountPriceLabel}</small>
									{formatCurrency(prices.onetimePayable!, site.locale.code)}
									<small>
										{translations.discountRegularPriceLabel.replace(
											'%price%',
											formatCurrency(prices.onetime, site.locale.code)
										)}
									</small>
								</div>
							{:else}
								<div class="aio-step-header-price-value">
									{formatCurrency(prices.onetime, site.locale.code)}
								</div>
							{/if}
						{:else if !configuratorState.isValidQuantity() && configuratorState.hasFromPrices() && configuratorState.selectedFrequency() > 0 && fromPrices.recurring !== null}
							<div class="aio-step-header-price-value">
								<!-- <small>{translations.fromPriceLabel}</small> -->
								{formatCurrency(fromPrices.recurringPayable!, site.locale.code)}
							</div>
						{:else if !configuratorState.isValidQuantity() && configuratorState.hasFromPrices() && configuratorState.selectedFrequency() === 0 && fromPrices.onetime !== null}
							<div class="aio-step-header-price-value">
								<!-- <small>{translations.fromPriceLabel}</small> -->
								{formatCurrency(fromPrices.onetimePayable!, site.locale.code)}
							</div>
						{:else if !configuratorState.usesTotalPrice}
							<div class="aio-step-header-price-value">
								{formatCurrency(0, site.locale.code)}
							</div>
						{/if}

						{#if prices.recurringDiscount <= 0}
							<div class="aio-step-header-price-frequency">
								{#if configuratorState.selectedFrequency() > 0}
									/
									{formatNumericString(
										translations.frequencySingle,
										translations.frequencyMultiple,
										configuratorState.selectedFrequency(),
										'%quantity%'
									)}
								{/if}
							</div>
						{/if}

						{#if site.showWeightPrices && prices.recurring !== null}
							<div class="aio-step-header-price-per-unit">
								{formatCurrency(
									prices.recurringPayable! /
										(targetQuantity * configuratorState.quantityMultiplier()),
									site.locale.code
								)}
								{translations.perUnitText.replaceAll(
									'%product%',
									selectedCategory?.translations.unitNameSingle ?? '%product%'
								)}
							</div>
						{:else if site.showWeightPrices && !configuratorState.isValidQuantity() && configuratorState.hasFromPrices() && fromPrices.recurring !== null && fromDisplayQuantity > 0}
							<div class="aio-step-header-price-per-unit">
								{translations.fromPriceLabel}
								{formatCurrency(
									fromPrices.recurringPayable! / fromDisplayQuantity,
									site.locale.code
								)}
								{translations.perUnitText.replaceAll(
									'%product%',
									selectedCategory?.translations.unitNameSingle ?? '%product%'
								)}
							</div>
						{/if}
					{/if}
				</div>
			{/if}
		</div>
		{#if targetQuantity > 0}
			{#if configurator.portionsTooltip.show && configuratorState.selectedFrequency() > 0}
				<PortionSuggestions
					portions={configuratorState.products()[0]?.product?.portions * targetQuantity}
					frequency={configuratorState.selectedFrequency()}
					days={configurator.portionsTooltip.amountOfPortionsPeriodDays}
					translations={{
						totalSingle: configurator.portionsTooltip.amountOfPortionsTotalSingle,
						totalPlural: configurator.portionsTooltip.amountOfPortionsTotalPlural,
						perPeriodSingle: configurator.portionsTooltip.amountOfPortionsPerPeriodSingle,
						perPeriodPlural: configurator.portionsTooltip.amountOfPortionsPerPeriodPlural
					}}
				/>
			{/if}
			<div class="aio-step-content">
				<h3 class="aio-step-main-title">
					{configurator.productSelectionTitle}
				</h3>
				<div
					class="aio-step-sliders {selectedCategory
						? `aio-step-sliders--${selectedCategory.id}`
						: ''}"
				>
					{#each configuratorState.products() as { product, quantity }}
						<div class="aio-step-slider" data-product-external-id={product.id}>
							<div class="aio-step-slider-label aio-step-slider-label--{product.category.icon}">
								{product.name}
							</div>
							<div class="aio-step-slider-range">
								<RangeSlider
									min={0}
									max={targetQuantity * configuratorState.quantityMultiplier()}
									step={configuratorState.quantityMultiplier()}
									value={quantity * configuratorState.quantityMultiplier()}
									onChange={(e) =>
										configuratorState.setProductQuantity(
											product,
											parseInt(e.target.value) / configuratorState.quantityMultiplier()
										)}
								/>
							</div>
							<div class="aio-step-slider-value">
								{quantity * configuratorState.quantityMultiplier()}
							</div>
						</div>
					{/each}
				</div>
				<div class="aio-step-totals">
					{#if selectedQuantity !== targetQuantity}
						<div class="aio-step-totals-error">
							<QuantityValidationMessage
								quantities={[targetQuantity]}
								quantity={configuratorState.selectedQuantity()}
								multiplier={configuratorState.quantityMultiplier()}
								{...selectedCategory?.translations}
							/>
						</div>
					{/if}
					<div class="aio-step-totals-count">
						<span class="aio-step-totals-count-value">
							{selectedDisplayQuantity}
						</span>
						/
						<span class="aio-step-totals-count-value">
							{targetQuantity * configuratorState.quantityMultiplier()}
						</span>
					</div>
				</div>
			</div>
		{/if}
		<div class="aio-step-button">
			<button
				class="button"
				disabled={!configuratorState.isValid() ||
					!configuratorState.canSubmitPricing() ||
					targetQuantity !== selectedQuantity}
				onclick={handleSubmit}
			>
				{configurator.orderButtonText}
			</button>
		</div>
		{#if site.showWeightPrices && prices}
			<div class="aio-step-volume-price">
				<VatLabel />
				<small>
					{#if configuratorState.selectedFrequency() > 0 && prices.recurring !== null}
						{formatCurrency(
							prices.recurringPayable! /
								(targetQuantity * configuratorState.quantityMultiplier()) /
								(configuratorState?.selectedCategory()?.rawProductWeight ?? 0),
							site.locale.code
						)} / 1kg
					{:else if prices.onetime !== null && site.showWeightPrices}
						{formatCurrency(
							prices.onetimePayable! /
								(targetQuantity * configuratorState.quantityMultiplier()) /
								(configuratorState?.selectedCategory()?.rawProductWeight ?? 0),
							site.locale.code
						)} / 1kg
					{/if}
				</small>
			</div>
		{/if}
	</div>
</div>

<style lang="postcss">
	/* From AllInOneStep.svelte */
	.aio-step {
		& {
			@media (--lg) {
				display: flex;
			}
		}

		&-info {
			margin-bottom: var(--spacing-7);
			@media (--lg) {
				width: 356px;
				padding-right: var(--spacing-10);
			}

			&-title {
				margin-bottom: var(--spacing-4);
			}
		}

		&-main {
			flex: 1;

			&-title {
				margin-bottom: var(--spacing-4) !important;
			}
		}

		&-header {
			& {
				width: 100%;
			}

			@media (--sm) {
				display: flex;
				align-items: center;
			}

			&-selects {
				width: 100%;
				margin-bottom: var(--spacing-6);
				@media (--sm) {
					display: flex;
					align-self: flex-start;
					justify-content: space-between;
					max-width: 60%;
					margin-bottom: 0;
				}
			}

			&-select {
				@media (--sm) {
					width: calc(50% - 7px);
				}

				&:not(:last-child) {
					margin-bottom: var(--spacing-3);

					@media (--sm) {
						margin-bottom: 0;
					}
				}
			}

			&-price {
				flex: 1;
				display: flex;
				flex-wrap: wrap;
				align-items: flex-end;
				line-height: var(--font-lineheight-0);

				@media (--sm) {
					justify-content: flex-end;
				}

				&-value {
					margin-left: auto;
					font-size: var(--font-size-4);

					small {
						display: block;
						font-size: var(--font-size-0);
						color: var(--color-grey);
					}

					&--discount {
						text-align: right;
						line-height: var(--font-lineheight-1);
					}
				}

				&-frequency {
					margin-left: 4px;
					font-size: var(--font-size-0);
					color: var(--color-grey);
				}

				&-per-unit {
					display: block;
					width: 100%;
					font-size: 13px;
					text-align: right;
					color: var(--color-brown);
					margin-top: var(--spacing-1);
				}
			}
		}

		&-content {
			padding-top: var(--spacing-6);
		}

		&-sliders {
			&:not(:last-child) {
				margin-bottom: var(--spacing-6);
			}
		}

		&-slider {
			position: relative;
			font-size: var(--font-size-0);

			@media (--sm) {
				display: flex;
				align-items: center;
				font-size: var(--font-size-1);
			}

			&:not(:last-child) {
				margin-bottom: var(--spacing-5);

				@media (--sm) {
					margin-bottom: var(--spacing-6);
				}
			}

			&-label {
				display: flex;
				align-items: center;
				font-family: var(--step-slider-label-font-family);
				font-size: var(--step-slider-label-font-size);
				font-weight: var(--step-slider-label-font-weight);
				color: var(--step-slider-label-color);
				text-transform: var(--step-slider-label-text-transform);
				margin-bottom: 4px;

				@media (--sm) {
					width: 200px;
					margin-bottom: 0;
				}

				&--cup,
				&--bean {
					&:before {
						content: '';
						display: block;
						width: 32px;
						height: 32px;
						margin-right: var(--spacing-3);
						background-image: url('data:image/svg+xml;base64,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');
						background-size: cover;
						transform: rotate(45deg);
					}
				}

				&--bean {
					&:before {
						width: 28px;
						height: 21px;
						transform: none;
						background-image: url('data:image/svg+xml;base64,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');
					}
				}
			}

			&-range {
				@media (--sm) {
					flex: 1;
				}
			}

			&-value {
				position: absolute;
				z-index: 2;
				top: 0;
				right: 0;
				text-align: right;
				font-family: var(--font-secondary);
				font-weight: normal;

				@media (--sm) {
					position: relative;
					top: auto;
					right: auto;
					margin-left: var(--spacing-4);
				}
			}
		}

		&-totals {
			position: relative;
			display: flex;
			justify-content: flex-end;
			text-align: right;
			padding-top: var(--spacing-4);
			margin-bottom: var(--spacing-6);

			&:before {
				content: '';
				width: 60px;
				height: 2px;
				display: block;
				position: absolute;
				top: 0;
				right: 0;
				background-color: var(--color-primary);
			}

			&-error {
				position: relative;
				margin-right: var(--spacing-3);
				font-size: 13px;
				text-align: right;
				line-height: var(--font-lineheight-1);
				padding: 10px 12px 10px 35px;
				color: white;
				white-space: nowrap;
				background-color: var(--color-red);
				background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNCI+PHBhdGggZmlsbD0iI0ZGRiIgZmlsbC1ydWxlPSJldmVub2RkIiBkPSJtOCAwIDggMTRIMEw4IDBaTTcgNC45NzZjMC0uNTU3LjQ0NC0xLjAxIDEtMS4wMS41NTIgMCAxIC40NDggMSAxLjAxdjMuOTMxYzAgLjU1OC0uNDQ0IDEuMDEtMSAxLjAxLS41NTIgMC0xLS40NDgtMS0xLjAxVjQuOTc2Wk03IDExLjlhLjk5My45OTMgMCAwIDEgMS0uOTkyYy41NTIgMCAxIC40NCAxIC45OTJhLjk5My45OTMgMCAwIDEtMSAuOTkyYy0uNTUyIDAtMS0uNDQtMS0uOTkyWiIvPjwvc3ZnPg==');
				background-size: 16px 14px;
				background-position: 11px calc(50% - 1px);
				background-repeat: no-repeat;
				border-radius: var(--radius-2);

				&:after {
					content: '';
					position: absolute;
					top: calc(50% - 8px);
					right: -6px;
					width: 0;
					height: 0;
					border-top: 8px solid transparent;
					border-bottom: 8px solid transparent;
					border-left: 8px solid var(--color-red);
				}
			}

			&-count {
				font-size: var(--font-size-2);
				text-transform: uppercase;
				position: relative;
				display: flex;
				align-items: center;

				&-value {
					color: var(--color-brown);
					font-family: var(--font-secondary);
					font-weight: normal;
				}
			}
		}

		&-button {
			display: flex;
			justify-content: flex-end;
		}

		&-volume-price {
			display: flex;
			justify-content: flex-end;
			margin-top: var(--spacing-4);
		}
	}
</style>
